import React, { type ChangeEvent, useEffect, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
    generateFunRandomPodName,
    getPodDefaultData,
} from '@pkg/components/Sections/Groups/GroupsNavigation/utilities';
import { useCreateGroup } from '@pkg/hooks';

import config from '@app/config';
import { GROUP_BASE } from '@app/routes/paths/groups';
import { PathParams } from '@app/routes/types';

import { CreatePodForm, CreatePodSuccess } from './components';

const POD_NAME_LIMIT = config.podNameLimit;

export interface CreateNewPodModalProps {
    // TODO: Remove `show` once `CreatePodSuccess`
    // has been properly refactored.
    show?: boolean;
    onClose(): void;
}

export function CreateNewPodModal({ show = false, onClose }: CreateNewPodModalProps) {
    const { t: translate } = useTranslation(['groups']);

    const { [PathParams.GroupId]: groupId } = useParams();
    const navigate = useNavigate();

    const { mutate, isLoading } = useCreateGroup();

    const [podName, setPodName] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [podNameGenerated, setPodNameGenerated] = useState(false);

    const location = useLocation();
    const stateLocation = location.state as { newPodId?: string };
    // TODO: Why is this using loose equality?
    const isNewPod = groupId && groupId == stateLocation?.newPodId;

    // `showSuccessScreen` is used so that if the user closes
    // the success screen and clicks on `create` again, it would be
    // presented with the creation modal even though the `newPodId`
    // is introduced in the location state.
    const [showSuccessScreen, setShowSuccessScreen] = useState(isNewPod);

    const isCancelled = useRef(false);
    useEffect(() => {
        isCancelled.current = false;
        const controller = new AbortController();
        const signal = controller.signal;
        const handleGenerateName = async () => {
            const podName = await generateFunRandomPodName(signal);
            if (!isCancelled.current) {
                setPodName(podName);
                setPodNameGenerated(true);
            }
        };
        if (!podNameGenerated) handleGenerateName();
        return () => {
            isCancelled.current = true;
            controller.abort();
        };
    }, [isNewPod, podNameGenerated]);

    function handleCreatePod() {
        if (isLoading || !podName || podName.length < 3) return false;

        const podData = getPodDefaultData(podName, isPrivate);

        mutate(podData, {
            onSuccess({ slug }) {
                // Adds `newPodId` to automatically open this modal upon redirect.
                // This would allow us to add the invite flow easily
                // since it would be handled by the same component without
                // the need of redux or context being introduced.
                if (slug) navigate(`${GROUP_BASE}/${slug}`, { state: { newPodId: slug } });

                setShowSuccessScreen(true);
                setPodName('');
            },
            onError({ cause }) {
                // TODO: Fix the typing on this argument.
                const message =
                    (cause as { name?: string })?.name === 'Forbidden'
                        ? translate('pod_creation_pod_name_input_error_forbidden')
                        : translate('pod_creation_pod_name_input_error');

                setErrorMessage(message);
            },
        });
    }

    function handleClose() {
        // Prevent closing if creating a new POD.
        if (isLoading) return;

        // Prevents success screen if user lands on a
        // newly created POD and clicks on create pod again.
        setShowSuccessScreen(false);

        // TODO: This can be removed once we have decoupled
        // the Modals and promote <AnimatePresence /> to the parent.
        setPodNameGenerated(false);

        // If new pod, replace location state.
        if (isNewPod) navigate(`.`, { replace: true });

        onClose();
    }

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value.length <= POD_NAME_LIMIT) setPodName(event.target.value);
    };

    const handleCheckboxChange = (checked: boolean) => {
        setIsPrivate(checked);
    };

    // TODO: These two Modals should be decoupled,
    // and conditionally rendered from the parent.
    const modalMarkup = showSuccessScreen ? (
        <CreatePodSuccess key="CreateNewPodModal-Success" onConfirm={handleClose} />
    ) : (
        <CreatePodForm
            key="CreateNewPodModal-Form"
            podName={podName}
            errorMessage={errorMessage}
            isPrivate={isPrivate}
            isLoading={isLoading || !podNameGenerated}
            onClose={handleClose}
            onCreatePod={handleCreatePod}
            onInputChange={handleInputChange}
            onCheckedChange={handleCheckboxChange}
        />
    );

    return <AnimatePresence mode="wait">{show ? modalMarkup : null}</AnimatePresence>;
}
